import React from "react";
import RadioSelect from "../../../../components/RadioSelect/RadioSelect";
import { RiInformationLine } from "react-icons/ri";

interface Props {
  idPrefix?: string;
  isSelected: boolean;
  onSelect: () => void;
  isDisabled?: boolean;
  title: string;
  description?: React.ReactNode;
  boldDescription?: string;
  headerImageSrc?: string;
  headerIconSrc?: string;
  badgeText?: string;
  badgeColor?: string;
  isPopupActive?: boolean;
  alwaysShowBody?: boolean;
  noSelectedBackground?: boolean;
  showHeaderSeparator?: boolean;
  noDescriptionIndent?: boolean;
  hasError?: boolean;
  /** Care+ accent: orange radio instead of the default green. */
  orange?: boolean;
}

const OptionCardVU: React.FC<Props> = ({
  idPrefix,
  isSelected,
  onSelect,
  isDisabled,
  title,
  description,
  boldDescription,
  headerImageSrc,
  headerIconSrc,
  badgeText,
  badgeColor,
  isPopupActive,
  alwaysShowBody,
  noSelectedBackground,
  showHeaderSeparator,
  noDescriptionIndent,
  hasError,
  orange = false,
}) => {
  const getId = (suffix: string) => `${idPrefix || "option-card"}-${suffix}`;

  
  return (
    <div
      id={getId("container")}
      className={`relative flex flex-col p-4 mb-2 ${badgeText ? "mt-8" : "mt-2"} w-full select-none rounded-md ${isDisabled ? "cursor-not-allowed" : "cursor-pointer"} `}
      style={{
        backgroundColor: isDisabled ? "#EEF1F6" : (isSelected && !isPopupActive && !noSelectedBackground ? "#F7FAEF" : "transparent"),
        border: hasError ? "1px solid #e53e3e" : "1px solid #EEF1F6",
      }}
      onClick={() => isDisabled ? null : onSelect()}
    >
      
      {/* Header */}
      <div id={getId("header")} className="flex items-center justify-between gap-2">
        <div className="flex items-center clas">
          {
            headerIconSrc ?
              <div id={getId("icon-container")} className='flex rounded-full p-1 bg-[#C0F6EA] select-none mr-2'>
                <img src={headerIconSrc} alt='Pharmacy' className='w-[30px] h-[30px] object-cover' />
              </div>
              :
              <RadioSelect
                backgroundColor={orange ? "#f08e00" : "#269C6E"}
                borderColor={orange ? null : "#c6f4e9"}
                selected={isSelected}
                disabled={false}
              />
          }
        
          <p id={getId("title")} className={`font-poppins text-sm font-medium ${isDisabled ? "text-[#98a0af]" : "text-[#0d2c54]"}`}>{title}</p>
        </div>

        {headerImageSrc && <img src={headerImageSrc} alt="icon" className="h-[30px] max-w-[60px] object-contain" />}

      </div>

      {/* Separator between header and body */}
      {showHeaderSeparator && (isSelected || alwaysShowBody) && (
        <div style={{ borderBottom: "1px solid #dee3ed", margin: "16px -16px 0" }} />
      )}

      {/* Body  */}
      {
        (isSelected || alwaysShowBody) ?
          <div id={getId("body")} className={`flex flex-col ${showHeaderSeparator ? "mt-4" : "mt-2"} gap-y-[5px] z-[3]`}>
            {/* Description */}
            {description && (
              <div id={getId("description")} className={`font-poppins ${noDescriptionIndent ? "" : "pl-[27px]"} font-normal text-[13px] leading-[19.5px] text-[#656d79] flex flex-col gap-[8px]`}>{typeof description === 'string' ? description.split('\n').map((line: string, i: number) => <p key={i}>{line}</p>) : description}</div>
            )}

            {/* Bold Description */}
            {boldDescription && (
              <div id={getId("bold-description")} className="font-roboto pl-[27px] font-semibold text-[13px] text-[#656d79] ">{boldDescription}</div>
            )}

          </div>
          :
          null
      }

      {/* Badge */}
      {badgeText && (
        <span id={getId("badge")} style={{backgroundColor: badgeColor}} className={`absolute top-[-26px] right-[15px] flex items-center gap-1 text-white px-2 py-1 rounded-tl-md rounded-tr-md text-[12px] z-1`}>
          <RiInformationLine size={14} id={getId("info-icon")} />
          {badgeText}
        </span>
      )}

    </div>
  );
};

export default OptionCardVU;
